import axios from "axios";

import { readFile } from "../lib/file";
import { Workspace } from "./workspaces";

export enum UploadStatus {
  Pending = "pending",
  Uploaded = "uploaded",
  Failed = "failed",
}

export interface Upload {
  id: number;
  "workspace-id": number;
  "user-id": number;
  thumbnails: string[];
  title: string;
  description: string;
  "original-filename": string;
  filename: string;
  "content-type": string;
  size: number;
  status: UploadStatus;
  "created-at": string;
  "updated-at": string;
}

export interface CreateUploadData {
  file: File;
  title: string;
  description: string;
  thumbnails?: string[];
}

export interface UpdateUploadData {
  file?: File;
  title: string;
  description: string;
  thumbnails?: string[];
}

export interface UploadBenefit {
  "upload-id": number;
  "benefit-external-id": string;
}

export interface UploadWithStats extends Upload {
  patrons: number;
  benefits: UploadBenefit[];
}

export const getWorkspaceUploads = async (
  workspace: Workspace,
): Promise<UploadWithStats[]> => {
  return (await axios.get(`/api/v1/workspaces/${workspace.id}/uploads`)).data;
};

const maxPartSize = 10 * 1024 * 1024;
const uploadParts = async (
  workspace: Workspace,
  file: File,
  progress: (progress: number) => void = () => {},
): Promise<{ parts: string; blob: Blob }> => {
  let lo = 0;
  const buffer = await readFile(file);
  const parts: string[] = [];
  while (lo < buffer.byteLength) {
    const hi = Math.min(lo + maxPartSize, buffer.byteLength);
    const form = new FormData();
    const blob = new Blob([buffer.slice(lo, hi)]);
    form.append("part", blob);
    const partName = (
      await axios.post(`/api/v1/workspaces/${workspace.id}/parts`, form)
    ).data;
    parts.push(partName);
    lo = hi;
    progress(lo / buffer.byteLength);
  }
  return {
    blob: new Blob([], { type: file.type }),
    parts: parts.join(";"),
  };
};

export const createWorkspaceUpload = async (
  workspace: Workspace,
  data: CreateUploadData,
  progress: (progress: number) => void = () => {},
): Promise<Upload> => {
  const formData = new FormData();
  formData.append("title", data.title);
  formData.append("description", data.description);
  if (data.thumbnails !== undefined) {
    formData.append("thumbnails", JSON.stringify(data.thumbnails));
  }

  progress(0.0);
  if (data.file.size > maxPartSize) {
    const { parts, blob } = await uploadParts(workspace, data.file, progress);
    formData.append("parts", parts);
    formData.append("file", blob, data.file.name);
  } else {
    formData.append("file", data.file);
  }

  const resp = await axios.post(
    `/api/v1/workspaces/${workspace.id}/uploads`,
    formData,
  );
  progress(1.0);
  return resp.data;
};

export const patchWorkspaceUpload = async (
  workspace: Workspace,
  uploadId: number,
  data: UpdateUploadData,
  progress: (progress: number) => void = () => {},
): Promise<Upload> => {
  const formData = new FormData();
  formData.set("title", data.title);
  formData.set("description", data.description);
  if (data.thumbnails !== undefined) {
    formData.append("thumbnails", JSON.stringify(data.thumbnails));
  }

  progress(0.0);
  if (data.file !== undefined) {
    if (data.file.size > maxPartSize) {
      const { parts, blob } = await uploadParts(workspace, data.file, progress);
      formData.append("parts", parts);
      formData.append("file", blob, data.file.name);
    } else {
      formData.set("file", data.file);
    }
  }

  const resp = await axios.put(
    `/api/v1/workspaces/${workspace.id}/uploads/${uploadId}`,
    formData,
  );
  progress(1.0);
  return resp.data;
};

export const deleteWorkspaceUpload = async (
  workspace: Workspace,
  uploadId: number,
): Promise<void> => {
  await axios.delete(`/api/v1/workspaces/${workspace.id}/uploads/${uploadId}`);
};

export const getWorkspaceUploadDownloadUrl = (
  workspace: Workspace,
  uploadId: number,
): string => {
  return `/api/v1/workspaces/${workspace.id}/uploads/${uploadId}`;
};

export const getWorkspaceUploadBenefits = async (
  workspace: Workspace,
  uploadId: number,
): Promise<UploadBenefit[]> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspace.id}/uploads/${uploadId}/benefits`,
    )
  ).data;
};

export const updateWorkspaceUploadBenefits = async (
  workspace: Workspace,
  uploadId: number,
  benefits: string[],
): Promise<void> => {
  await axios.put(
    `/api/v1/workspaces/${workspace.id}/uploads/${uploadId}/benefits`,
    { benefits },
  );
};
